import { createBrowserRouter } from "react-router-dom";
import { lazy, Suspense } from "react";
import { PencomRoutes } from "./PencomRoutes";
import PencomLayout from "@/components/layout/PencomLayout";
import OnboardingLayout from "@/components/layout/OnboardingLayout";
import OnboardingLayoutWithSupport from "@/components/layout/OnboardingLayoutWithSupport";
import VerificationPage from "@/pages/onboarding/VerificationPage";
import { EmployerDetailsPage } from "@/pages/onboarding/EmployerDetailsPage";
import { EmployerRepRegistrationSuccessPage } from "@/pages/onboarding/EmployerRepRegistrationSuccessPage";
import {
  AuthorizedContactVerificationPage,
  EmployeeVerificationPage,
} from "@/pages/onboarding/EmployeeVerificationPage";
import { EmployerCodeRegPage } from "@/pages/onboarding/EmployerCodeRegPage";
import { EmployerRepPage } from "@/pages/onboarding/EmployerRepPage";
import OnboardingCompletionPage from "@/pages/onboarding/OnboardingCompletionPage";
import VerifyCompanyDetails from "@/pages/onboarding/VerifyCompanyDetails";
import LoginPage from "@/pages/onboarding/LoginPage";
import EmployerRegistrationVerificationPage from "@/pages/onboarding/EmployerRegistrationVerificationPage";
import EmployerRegEmailVerificationPage from "@/pages/onboarding/EmployerRegEmailVerificationPage";
import EmployerRegistrationSuccessPage from "@/pages/onboarding/EmployerRegistrationSuccessPage";
import EmployerRepVerificationPage from "@/pages/onboarding/EmployerRepVerificationPage";
import PublicCertificateViewPage from "@/pages/public/PublicCertificateViewPage";
import ComplianceVerificationPage from "@/pages/public/ComplianceVerificationPage";
import ReportDiscrepanciesPage from "@/pages/onboarding/ReportDiscrepanciesPage";
import ContactUpdatePage from "@/pages/onboarding/ContactUpdatePage";
import ReportSubmissionSuccessPage from "@/pages/onboarding/ReportSubmissionSuccessPage";
import PlainLayout from "@/components/layout/PlainLayout";
import MobileAwareRoot from "@/components/layout/MobileAwareRoot";
import { RouteFallback } from "@/components/common/RouteFallback";
import ErrorPage from "@/pages/ErrorPage";
import PenaltyBreakdownPage from "@/pages/dashboard/PenaltyBreakdownPage";

const Dashboard = lazy(() => import("@/pages/dashboard/Dashboard"));
const TutorialsPage = lazy(() => import("@/pages/tutorials/TutorialsPage"));
const SupportPage = lazy(() => import("@/pages/dashboard/SupportPage"));
const SupportTicketsListPage = lazy(
  () => import("@/pages/dashboard/SupportTicketsListPage")
);
const SupportTicketDetailPage = lazy(
  () => import("@/pages/dashboard/SupportTicketDetailPage")
);

import { AddGroupLifeInsurancePage } from "@/pages/dashboard/AddGroupLifeInsurancePage";
import { AvailableProvidersPage } from "@/pages/dashboard/AvailableProvidersPage";
import { GroupLifeInsurancePage } from "@/pages/dashboard/GroupLifeInsurancePage";
import { ContributionHistoryPage } from "@/pages/dashboard/ContributionsHistoryPage";
import ContributionsByMonthPage from "@/pages/dashboard/ContributionsByMonthPage";
import { RequestCallBackPage } from "@/pages/dashboard/RequestCallBackPage";
import { AddEmployeePage } from "@/pages/dashboard/AddEmployeePage";
import { BulkUploadEmployeePage } from "@/pages/dashboard/BulkUploadEmployeePage";
import ApplyForPCCPage from "@/pages/pcc/ApplyForPCCPage";
import PCCApplicationSuccessfulPage from "@/pages/pcc/PCCApplicationSuccessfulPage";
import PCCCertificateViewPage from "@/pages/pcc/PCCCertificateViewPage";
import ReviewComplianceEvaluation from "@/pages/pcc/ReviewComplianceEvaluation";
import { EmployeeListPage } from "@/pages/dashboard/EmployeeListPage";
import EmployerUpdateRequestPage from "@/pages/dashboard/EmployerUpdateRequestPage";

/**
 * Child routes under `path: "/"` must be relative (no leading `/`).
 * Using `/penalty-breakdown` there breaks matching on refresh / direct URL entry.
 */
const nest = (path: string) => path.replace(/^\//, "");

export const PencomRouter = createBrowserRouter([
  {
    element: <MobileAwareRoot />,
    errorElement: <ErrorPage />,
    children: [
      {
        path: PencomRoutes.PUBLIC_CERTIFICATE_VIEW,
        element: <PublicCertificateViewPage />,
      },
      {
        path: PencomRoutes.VERIFY_COMPLIANCE,
        element: <ComplianceVerificationPage />,
      },
      {
        path: PencomRoutes.TUTORIALS,
        element: (
          <Suspense fallback={<RouteFallback />}>
            <TutorialsPage />
          </Suspense>
        ),
      },
      {
        path: PencomRoutes.TUTORIAL_VIDEO,
        element: (
          <Suspense fallback={<RouteFallback />}>
            <TutorialsPage />
          </Suspense>
        ),
      },
      {
        element: <PlainLayout />,
        children: [
          {
            path: PencomRoutes.VERIFICATION,
            element: <VerificationPage />,
          },
          {
            path: PencomRoutes.CONTACT_UPDATE,
            element: <ContactUpdatePage />,
          },
          {
            path: PencomRoutes.VERIFY_COMPANY_DETAILS,
            element: <VerifyCompanyDetails />,
          },
          {
            path: PencomRoutes.EMPLOYER_REP_DETAILS,
            element: <EmployerRepPage />,
          },
          {
            path: PencomRoutes.EMPLOYER_REG_VERIFICATION,
            element: <EmployeeVerificationPage />,
          },
          {
            path: PencomRoutes.AUTHORIZED_CONTACT_VERIFICATION,
            element: <AuthorizedContactVerificationPage />,
          },
          {
            path: PencomRoutes.ONBOARDING_COMPLETION,
            element: <OnboardingCompletionPage />,
          },
          {
            path: PencomRoutes.EMPLOYER_REG_OTP_VERIFICATION,
            element: <EmployerRegistrationVerificationPage />,
          },
          {
            path: PencomRoutes.REPORT_DISCREPENCY,
            element: <ReportDiscrepanciesPage />,
          },
          {
            path: PencomRoutes.REPORT_SUBMISSION_SUCCESS,
            element: <ReportSubmissionSuccessPage />,
          },
          {
            path: PencomRoutes.EMPLOYER_REP_VERIFICATION,
            element: <EmployerRepVerificationPage />,
          },
        ],
      },
      {
        element: <OnboardingLayout />,
        children: [
          {
            path: PencomRoutes.LOGIN,
            element: <LoginPage />,
          },
        ],
      },

      {
        path: PencomRoutes.DASHBOARD,
        element: <PencomLayout />,
        children: [
          {
            index: true,
            element: (
              <Suspense fallback={<RouteFallback />}>
                <Dashboard />
              </Suspense>
            ),
          },
          {
            path: nest(PencomRoutes.EMPLOYEE_LIST),
            element: (
              <Suspense fallback={<RouteFallback />}>
                <EmployeeListPage />
              </Suspense>
            ),
          },
          {
            path: nest(PencomRoutes.EMPLOYER_UPDATE_REQUEST),
            element: <EmployerUpdateRequestPage />,
          },
          {
            path: nest(PencomRoutes.ADD_GROUP_LIFE_INSURANCE),
            element: <AddGroupLifeInsurancePage />,
          },
          {
            path: nest(PencomRoutes.AVAILABLE_PROVIDERS),
            element: (
              <Suspense fallback={<RouteFallback />}>
                <AvailableProvidersPage />
              </Suspense>
            ),
          },
          {
            path: nest(PencomRoutes.GROUP_LIFE_INSURANCE),
            element: (
              <Suspense fallback={<RouteFallback />}>
                <GroupLifeInsurancePage />
              </Suspense>
            ),
          },
          {
            path: nest(PencomRoutes.CONTRIBUTION_HISTORY),
            element: (
              <Suspense fallback={<RouteFallback />}>
                <ContributionHistoryPage />
              </Suspense>
            ),
          },
          {
            path: nest(PencomRoutes.PENALTY_BREAKDOWN),
            element: <PenaltyBreakdownPage />,
          },
          {
            path: nest(PencomRoutes.CONTRIBUTION_BY_MONTH),
            element: (
              <Suspense fallback={<RouteFallback />}>
                <ContributionsByMonthPage />
              </Suspense>
            ),
          },
          {
            path: nest(PencomRoutes.REQUEST_CALL_BACK),
            element: (
              <Suspense fallback={<RouteFallback />}>
                <RequestCallBackPage />
              </Suspense>
            ),
          },
          {
            path: nest(PencomRoutes.ADD_EMPLOYEE),
            element: (
              <Suspense fallback={<RouteFallback />}>
                <AddEmployeePage />
              </Suspense>
            ),
          },
          {
            path: nest(PencomRoutes.BULK_UPLOAD_EMPLOYEE),
            element: (
              <Suspense fallback={<RouteFallback />}>
                <BulkUploadEmployeePage />
              </Suspense>
            ),
          },
          {
            path: nest(PencomRoutes.APPLY_FOR_PCC),
            element: <ApplyForPCCPage />,
          },
          {
            path: nest(PencomRoutes.PCC_APPLICATION_SUCCESSFUL),
            element: <PCCApplicationSuccessfulPage />,
          },
          {
            path: nest(PencomRoutes.PCC_CERTIFICATE_VIEW),
            element: (
              <Suspense fallback={<RouteFallback />}>
                <PCCCertificateViewPage />
              </Suspense>
            ),
          },
          {
            path: nest(PencomRoutes.REVIEW_COMPLIANCE_EVALUATION),
            element: (
              <Suspense fallback={<RouteFallback />}>
                <ReviewComplianceEvaluation />
              </Suspense>
            ),
          },
          {
            path: nest(PencomRoutes.SUPPORT),
            element: (
              <Suspense fallback={<RouteFallback />}>
                <SupportPage />
              </Suspense>
            ),
          },
          {
            path: nest(PencomRoutes.SUPPORT_TICKETS),
            element: (
              <Suspense fallback={<RouteFallback />}>
                <SupportTicketsListPage />
              </Suspense>
            ),
          },
          {
            path: nest(PencomRoutes.SUPPORT_TICKET_DETAIL),
            element: (
              <Suspense fallback={<RouteFallback />}>
                <SupportTicketDetailPage />
              </Suspense>
            ),
          },
        ],
      },

      {
        path: PencomRoutes.ONBOARDING,
        element: <OnboardingLayout />,
        children: [
          {
            path: PencomRoutes.EMPLOYER_CODE_REG,
            element: <EmployerCodeRegPage />,
          },

          {
            path: PencomRoutes.EMPLOYER_REP_REG_SUCCESS,
            element: <EmployerRepRegistrationSuccessPage />,
          },
        ],
      },

      {
        element: <OnboardingLayoutWithSupport />,
        children: [
          {
            path: PencomRoutes.EMPLOYER_DETAILS,
            element: <EmployerDetailsPage />,
          },
          {
            path: PencomRoutes.EMPLOYER_REG_EMAIL_VERIFICATION,
            element: <EmployerRegEmailVerificationPage />,
          },
          {
            path: PencomRoutes.EMPLOYER_REG_SUCCESS,
            element: <EmployerRegistrationSuccessPage />,
          },
        ],
      },
    ],
  },
]);
